import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { http } from '@/lib/ats-https';
import { voiceAgentHttp } from '@/lib/voice-agent-http';
import type {
  ActiveSessionStatusParams,
  ActiveSessionStatusResponse,
  CoordinatorSessionByIdResponse,
  CoordinatorSessionResponse,
  CreateCoordinatorSessionPayload,
  CreateCoordinatorSessionResponse,
  CreateNoShowCoordinatorSessionPayload,
  CreateBulkCoordinatorSessionPayload,
  CreateBulkCoordinatorSessionResponse,
  Session,
  SessionsResponse,
  ValidateBulkParams,
  ValidateBulkResponse,
} from './types';

// ── API paths ──────────────────────────────────────────────────

const PATHS = {
  activeSessionStatus: '/coordinator_sessions/active_session_status',
  coordinatorSessions: '/coordinator_sessions',
  validateBulk: '/coordinator_sessions/validate_bulk',
  bulk: '/coordinator_sessions/bulk',
  voiceAgentSessions: '/sessions',
} as const;

/** Max number of polling refetches for coordinator session (stops to avoid indefinite polling). */
export const MAX_COORDINATOR_POLL_COUNT = 30;

/** Poll when call is scheduled within this many ms in the future (e.g. call in next 2 min). */
export const COORDINATOR_POLL_SCHEDULED_FUTURE_MS = 2 * 60 * 1000;
/** Poll when call was scheduled in the past within this many ms (call might still be ongoing). */
export const COORDINATOR_POLL_SCHEDULED_PAST_MS = 5 * 60 * 1000;

import {
  COORDINATOR_ADHOC_BLOCKING_STATUSES,
  COORDINATOR_POLL_STOP_STATUSES,
} from './coordinator-status';

export { COORDINATOR_ADHOC_BLOCKING_STATUSES, COORDINATOR_POLL_STOP_STATUSES };

export const COORDINATOR_POLL_INTERVAL = 5000;

/**
 * Delay (ms) before the first application-notes refetch after coordinator polling stops.
 * Gives the backend time to persist coordinator-generated notes.
 */
export const COORDINATOR_NOTES_REFETCH_DELAY_MS = 3000;

/** Delay (ms) between subsequent application-notes refetch attempts. */
export const COORDINATOR_NOTES_REFETCH_INTERVAL_MS = 3000;

/** Max application-notes refetch attempts after coordinator polling stops (includes the first). */
export const COORDINATOR_NOTES_REFETCH_MAX_ATTEMPTS = 10;

/**
 * True when session-by-id polling should stop (same rules as refetchInterval).
 * @param pollCount — current coordinatorPollCountRef value after interval runs.
 */
export function isCoordinatorSessionPollingStopped(
  data: CoordinatorSessionResponse | undefined | null,
  pollCount: number
): boolean {
  if (!data) {
    return false;
  }
  const status =
    data?.status !== undefined && data?.status !== null
      ? String(data.status).toLowerCase()
      : '';
  if (COORDINATOR_POLL_STOP_STATUSES.includes(status)) {
    return true;
  }
  if (data?.status === 'completed' && data?.ats_interview_id !== null) {
    return true;
  }
  const scheduledAt = data?.scheduled_at;
  if (scheduledAt) {
    const scheduledTime = new Date(scheduledAt).getTime();
    const now = Date.now();
    const diff = scheduledTime - now;
    if (diff > COORDINATOR_POLL_SCHEDULED_FUTURE_MS) {
      return true;
    }
    if (diff < 0 && -diff > COORDINATOR_POLL_SCHEDULED_PAST_MS) {
      return true;
    }
    if (pollCount > MAX_COORDINATOR_POLL_COUNT) {
      return true;
    }
  }
  return false;
}

// ── Query keys ─────────────────────────────────────────────────

export const coordinatorKeys = {
  activeSession: (params: ActiveSessionStatusParams) =>
    ['coordinator', 'activeSession', params] as const,
  sessions: (applicationId: number) =>
    ['coordinator', 'sessions', applicationId] as const,
  session: (sessionId: number) =>
    ['coordinator', 'session', sessionId] as const,
  /** ATS API GET /coordinator_sessions/:id (reschedule pre-fill, long polling) */
  sessionById: (sessionId: number) =>
    ['coordinator', 'sessionById', sessionId] as const,
  validateBulk: (params: ValidateBulkParams) =>
    ['coordinator', 'validateBulk', params] as const,
};

// ── Active session check ───────────────────────────────────────
// Checks if candidate already has an active coordinator session

export const useActiveSessionStatus = (
  params: ActiveSessionStatusParams,
  enabled = true
) =>
  useQuery({
    queryKey: coordinatorKeys.activeSession(params),
    queryFn: async (): Promise<ActiveSessionStatusResponse> => {
      const response = await http.get(PATHS.activeSessionStatus, {
        params,
      });
      return (response as any)?.data ?? response;
    },
    enabled: enabled && !!params.subject_id && !!params.ats_job_stage_id,
    staleTime: 0,
  });

// ── Create coordinator session (POST /coordinator_sessions) ─────

function invalidateSessionCache(
  queryClient: ReturnType<typeof useQueryClient>,
  sessionId: number | null
) {
  if (sessionId === null) return;
  queryClient.invalidateQueries({
    queryKey: coordinatorKeys.session(sessionId),
  });
  queryClient.invalidateQueries({
    queryKey: coordinatorKeys.sessionById(sessionId),
  });
}

export const useCreateCoordinatorSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateCoordinatorSessionPayload
    ): Promise<CreateCoordinatorSessionResponse> => {
      const res = await http.post(PATHS.coordinatorSessions, payload);
      return res as unknown as CreateCoordinatorSessionResponse;
    },
    onSuccess: (data, variables) => {
      const hasApplicationErrors =
        Array.isArray(data?.errors) && data.errors.length > 0;
      if (hasApplicationErrors) return;

      invalidateSessionCache(queryClient, data?.data?.id);
      queryClient.invalidateQueries({
        queryKey: coordinatorKeys.sessions(variables.subject_id),
      });
      if (
        variables.subject_type === 'ats_application' &&
        variables.subject_id !== null &&
        variables.ats_job_stage_id !== null
      ) {
        queryClient.invalidateQueries({
          queryKey: coordinatorKeys.activeSession({
            subject_type: 'ats_application',
            subject_id: variables.subject_id,
            ats_job_stage_id: variables.ats_job_stage_id,
          }),
        });
      }
    },
  });
};

// ── Create no-show coordinator session (POST /coordinator_sessions) ─────

export const useCreateNoShowCoordinatorSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateNoShowCoordinatorSessionPayload
    ): Promise<CreateCoordinatorSessionResponse> => {
      const res = await http.post(PATHS.coordinatorSessions, payload);
      return res as unknown as CreateCoordinatorSessionResponse;
    },
    onSuccess: (data) => {
      const hasApplicationErrors =
        Array.isArray(data?.errors) && data.errors.length > 0;
      if (hasApplicationErrors) return;
      invalidateSessionCache(queryClient, data?.data?.id);
    },
  });
};

// ── Fetch sessions for an application ──────────────────────────
// Uses voice-agent HTTP (different base URL)

export const useCoordinatorSessions = (applicationId: number, enabled = true) =>
  useQuery({
    queryKey: coordinatorKeys.sessions(applicationId),
    queryFn: async (): Promise<SessionsResponse> => {
      return voiceAgentHttp.get(PATHS.voiceAgentSessions, {
        params: { application_id: applicationId },
      });
    },
    enabled: enabled && !!applicationId,
    staleTime: 0,
  });

// ── Get session (GET voice agent /sessions/:id, for polling) ─────
// Aligned with kula/core-web: session-by-id and sessions list use voice agent API.

export const useCoordinatorSession = (
  sessionId: number | null,
  enabled = true,
  options?: { refetchInterval?: number | false }
) =>
  useQuery({
    queryKey: coordinatorKeys.session(sessionId!),
    queryFn: async (): Promise<Session> => {
      const res = await voiceAgentHttp.get(
        `${PATHS.voiceAgentSessions}/${sessionId}`
      );
      return ((res as any)?.data ?? res) as unknown as Session;
    },
    enabled: enabled && !!sessionId,
    refetchInterval:
      options?.refetchInterval !== undefined
        ? options.refetchInterval
        : enabled
          ? 3000
          : false,
    staleTime: 0,
  });

// ── Get session by ID (GET /coordinator_sessions/:id, ATS API) ───
// Use for reschedule pre-fill and for long polling (e.g. every 30s when immediate scheduling).
// refetchInterval can be a function: return false to stop (e.g. when status completed + ats_interview_id, or after MAX_COORDINATOR_POLL_COUNT).

export const useCoordinatorSessionById = (
  sessionId: number | null | undefined,
  options?: {
    enabled?: boolean;
    refetchInterval?:
      | number
      | false
      | ((_query: {
          state: { data?: CoordinatorSessionResponse };
        }) => number | false);
  }
) => {
  const id = sessionId !== null ? Number(sessionId) : 0;
  return useQuery({
    queryKey: coordinatorKeys.sessionById(id),
    queryFn: async (): Promise<CoordinatorSessionResponse> => {
      const res = await http.get<CoordinatorSessionByIdResponse>(
        `${PATHS.coordinatorSessions}/${sessionId}`
      );
      const body = (res as any)?.data ?? res;
      return (body?.data ?? body) as CoordinatorSessionResponse;
    },
    enabled: options?.enabled !== false && id > 0,
    refetchInterval: options?.refetchInterval ?? false,
    staleTime: 0,
  });
};

// ── Validate bulk (GET /coordinator_sessions/validate_bulk) ──────
// Request: GET {baseURL}/coordinator_sessions/validate_bulk?ats_job_stage_id=N&ats_application_ids[]=id1&ats_application_ids[]=id2
// baseURL from ats-https is /api_ats/v1 → full URL e.g. /api_ats/v1/coordinator_sessions/validate_bulk?...
// Not changed in bulk modal refactor; 404 = backend route missing or wrong proxy.

export const useValidateBulkCoordinatorSessions = (
  params: ValidateBulkParams | null,
  enabled: boolean
) =>
  useQuery({
    queryKey: coordinatorKeys.validateBulk(params!),
    queryFn: async (): Promise<ValidateBulkResponse> => {
      const requestParams = {
        ats_job_stage_id: params!.ats_job_stage_id,
        ats_application_ids: params!.ats_application_ids,
      };
      const response = await http.get(PATHS.validateBulk, {
        params: requestParams,
      });
      const body = response as {
        data?: ValidateBulkResponse;
        errors?: unknown[];
      };
      return body?.data ?? (body as unknown as ValidateBulkResponse);
    },
    enabled:
      enabled &&
      !!params?.ats_job_stage_id &&
      Array.isArray(params?.ats_application_ids) &&
      params.ats_application_ids.length > 0,
    staleTime: 0,
  });

// ── Create bulk (POST /coordinator_sessions/bulk) ────────────────

export const useCreateBulkCoordinatorSession = (options?: {
  onSuccess?: (_data: CreateBulkCoordinatorSessionResponse) => void;
}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (
      payload: CreateBulkCoordinatorSessionPayload
    ): Promise<CreateBulkCoordinatorSessionResponse> => {
      const res = await http.post(PATHS.bulk, payload);
      const body = res as { data?: CreateBulkCoordinatorSessionResponse };
      return (
        body?.data ?? (res as unknown as CreateBulkCoordinatorSessionResponse)
      );
    },
    onSuccess: (data, _variables) => {
      queryClient.invalidateQueries({ queryKey: ['bulk_activities'] });
      queryClient.invalidateQueries({ queryKey: ['coordinator'] });
      options?.onSuccess?.(data);
    },
  });
};

// ── Cancel session (POST /coordinator_sessions/:id/cancel) ──────

export const useCancelCoordinatorSession = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (sessionId: number) => {
      return http.post(`${PATHS.coordinatorSessions}/${sessionId}/cancel`);
    },
    onSuccess: (_data, sessionId) => {
      queryClient.invalidateQueries({ queryKey: ['coordinator'] });
      queryClient.invalidateQueries({
        queryKey: coordinatorKeys.session(sessionId),
      });
    },
  });
};
